import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {SavingCollaborationManagementModal} from 'collaborator-components/CollaborationManagementModal';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import makeCancelable from 'lib/makeCancelable';
import notifications, {Response} from 'notifications';
import {OrganizationPermissionRole} from 'orgs/OrganizationConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import omitKey from 'util/omitKey';
import {REQUEST_TOOK_TOO_LONG} from 'util/TaskRunner';

import SourceDAO, {UpdateData, UpdateSourceResponse} from './SourceDAO';

export function updateSourceCollaborations(
  id: string,
  collaborations: Array<Collaboration>
): Promise<UpdateSourceResponse> {
  const ownerType =
    collaborations.find((clb) => clb.fromOwnership)?.party.__typename === 'OrganizationNode'
      ? 'ORGANIZATION'
      : 'USER';
  const memberDataPolicyId =
    collaborations.find(
      (clb) => clb.fromOwnership && clb.organizationRole === OrganizationPermissionRole.MEMBER
    )?.policy.id ?? null;
  return updateSourceWithDataModifications(
    (dataModsHeader?: DataModificationsHeader) =>
      SourceDAO.update(
        id,
        {
          ownerInfo: {
            type: ownerType,
            memberDataPolicyId,
          },
          collaborations: convertToCollaborationCreateOrUpdates(
            collaborations.filter((c) => !c.fromOwnership)
          ),
        },
        dataModsHeader
      ),
    id
  );
}

/**
 * Updates the metadata of a "source" (project, registry).
 *
 * Updates to the source's review lifecycle will trigger a long running task. If the long running task does
 * not complete with a reasonable time, this method will return a successful promise and will continue to
 * process the update in the background.
 *
 * If the user cancels the update by closing the data modification modal, this method will return a rejected
 * promise with `isCanceled = True`.
 */
export function update(id: string, data: UpdateData): Promise<UpdateSourceResponse> {
  const updateSourceAndReviewLifecycle = async (
    dataModsHeader?: DataModificationsHeader
  ): Promise<UpdateSourceResponse> => {
    const updateData = {
      ...data,
      collaborations: data.collaborations?.filter((c) => !c.fromOwnership),
    };
    if (updateData.reviewLifecycleApiIds) {
      const [updateResponse, _] = await Promise.all([
        SourceDAO.update(id, omitKey(updateData, 'reviewLifecycleApiIds'), dataModsHeader),
        SourceDAO.setProjectReviewLifecycle({
          projectId: id,
          reviewLifecycleApiIds: updateData.reviewLifecycleApiIds,
          dataModsHeader,
        }).catch((res: Response) => {
          if (res.userMessage === REQUEST_TOOK_TOO_LONG) {
            notifications.warn(
              'Update to project Review Process is taking more than 10s, running in background.'
            );
            return Promise.resolve();
          } else {
            notifications.error(
              'Updates to review process failed, but other changes may have saved. Please close and reopen the Project Settings to see what changes were made.'
            );
            return Promise.reject(Error());
          }
        }),
      ]);
      return updateResponse;
    }

    return SourceDAO.update(id, omitKey(updateData, 'reviewLifecycleApiIds'), dataModsHeader);
  };

  return Object.keys(data).length
    ? updateSourceWithDataModifications(updateSourceAndReviewLifecycle, id)
    : updateSourceAndReviewLifecycle();
}

async function updateSourceWithDataModifications<T>(
  update: (dataModsHeader?: DataModificationsHeader) => Promise<T>,
  sourceId: string
): Promise<T> {
  let response: T | null = null;
  await callAsyncWithDataModifications({
    changeSummary: '1 project will be modified',
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      response = await update(dataModsHeader);
    },
    apiIds: [sourceId],
    analyticsParams: {
      module: 'PROJECT_FOLDER',
      // @ts-expect-error TODO: Should be using ActionOrigin type
      actionOrigin: 'project_settings_modal',
      modifiedAction: 'MODIFY',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS,
  });

  const promise = makeCancelable(Promise.resolve(response) as Promise<T | null>);
  if (response == null) {
    promise.cancel();
  }
  return promise.promise as Promise<T>;
}

export function deleteSource(id: string): JQueryPromise<void> {
  LoadingScreen.show();
  return SourceDAO.delete_(id)
    .always(() => {
      LoadingScreen.hide();
    })
    .fail(notifications.notifyError);
}

interface Source {
  id: string;
  reviewLifecyclesIds?: Array<string>;
}

function shouldRenderAuditorColumn(source: Source): boolean {
  if (
    BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES &&
    source.reviewLifecyclesIds !== undefined &&
    source.reviewLifecyclesIds.length > 0
  ) {
    return false;
  }
  return licenses.hasLicense('LICENSE_REVIEWS');
}

export function openSavingCollaborationManagementModal(source: Source): void {
  legacyRunModal(SavingCollaborationManagementModal, {
    policeableId: source.id,
    renderAuditorColumn: shouldRenderAuditorColumn(source),
    onRequestSubmit: (collaborations: Array<Collaboration>) =>
      updateSourceCollaborations(source.id, collaborations).then(() => {}),
  });
}
